import React, { createContext, useContext, useState, useEffect, useRef, ReactNode, useCallback } from 'react';
import {
  User,
  LoginRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  AuthResult,
  getCurrentUser,
  login as authLogin,
  logout as authLogout,
  forgotPassword as authForgotPassword,
  resetPassword as authResetPassword,
  validateToken,
  isAuthenticated as checkIsAuthenticated,
  isTokenExpiringSoon,
  refreshSecretaryAssignments as refreshSecretaryAssignmentsService
} from '../services/authService';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginRequest) => Promise<AuthResult>;
  logout: () => Promise<void>;
  forgotPassword: (request: ForgotPasswordRequest) => Promise<AuthResult>;
  resetPassword: (request: ResetPasswordRequest) => Promise<AuthResult>;
  /**
   * רענון רשימת הקצאות המזכירה מהשרת. נקרא בעלייה של האפליקציה ובכל ניווט לעמודי רשימה.
   * עבור משתמש שאינו מזכירה — לא עושה כלום.
   */
  refreshSecretaryAssignments: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Initialize auth state on app load
  useEffect(() => {
    const initializeAuth = async () => {
      setIsLoading(true);

      try {
        // Check if user is stored in localStorage and token is valid
        const storedUser = getCurrentUser();
        const isAuth = checkIsAuthenticated();

        if (storedUser && isAuth) {
          // Validate token with server
          const isValidToken = await validateToken();

          if (isValidToken) {
            setUser(storedUser);
            setIsAuthenticated(true);

            // אם המשתמש מזכירה — לרענן מיידית את רשימת ההקצאות לוודא טריות
            // (admin עלול לשנות הקצאות מאז ה-login האחרון)
            if (storedUser.permissionLevel === 4 /* Secretary */) {
              const refreshed = await refreshSecretaryAssignmentsService();
              if (refreshed) {
                setUser({ ...storedUser, assignedLeadIds: refreshed.leadIds, assignedClientIds: refreshed.clientIds });
              }
            }
          } else {
            // Token is invalid, clear auth state
            setUser(null);
            setIsAuthenticated(false);
          }
        } else {
          setUser(null);
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Check for token expiration periodically
  useEffect(() => {
    if (!isAuthenticated) return;

    const checkTokenExpiration = () => {
      if (isTokenExpiringSoon()) {
        // Show notification that session will expire soon
        console.warn('Session will expire soon');
        // In a real app, you might show a modal asking if user wants to extend session
      }
    };

    // Check every minute
    const interval = setInterval(checkTokenExpiration, 60000);

    return () => clearInterval(interval);
  }, [isAuthenticated]);

  const login = async (credentials: LoginRequest): Promise<AuthResult> => {
    setIsLoading(true);
    
    try {
      const result = await authLogin(credentials);
      
      if (result.isSuccess && result.data) {
        const userData = result.data.user;
        setUser(userData);
        setIsAuthenticated(true);
      }
      
      return result;
    } catch (error) {
      console.error('Login error:', error);
      return {
        isSuccess: false,
        message: 'שגיאה בהתחברות'
      };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    setIsLoading(true);
    
    try {
      await authLogout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      setIsLoading(false);
    }
  };

  const forgotPassword = async (request: ForgotPasswordRequest): Promise<AuthResult> => {
    return await authForgotPassword(request);
  };

  const resetPassword = async (request: ResetPasswordRequest): Promise<AuthResult> => {
    return await authResetPassword(request);
  };

  const userRef = useRef(user);
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const refreshSecretaryAssignments = useCallback(async (): Promise<void> => {
    if (!userRef.current || userRef.current.permissionLevel !== 4) return;
    const refreshed = await refreshSecretaryAssignmentsService();
    if (refreshed) {
      setUser(prev => prev
        ? { ...prev, assignedLeadIds: refreshed.leadIds, assignedClientIds: refreshed.clientIds }
        : prev);
    }
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    forgotPassword,
    resetPassword,
    refreshSecretaryAssignments
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};